import { THEME_VARIABLE } from '@/config';

/**
 * Backend status values for updating tickets via API
 * These are the values the backend expects when updating a ticket's status
 */
export enum TicketStatusEnum {
  OPEN = 'open',
  SOLVED = 'solved',
  PENDING = 'pending',
  ON_HOLD = 'hold',
}

/**
 * Normalized frontend status enum - the SINGLE source of truth for UI display
 */
export enum NormalizedTicketStatus {
  NEW = 'NEW',
  AWAITING_REPLY = 'AWAITING_REPLY',
  ON_HOLD = 'ON_HOLD',
  SOLVED = 'SOLVED',
}

/**
 * All possible backend status values from API responses
 */
export type BackendResponseStatus =
  | 'open'
  | 'pending'
  | 'on_hold'
  | 'solved'
  | 'closed'
  | 'in_progress'
  | 'waiting'
  | 'resolved';

/**
 * Backend status values used for filtering API requests
 */
export type BackendFilterStatus = 'open' | 'waiting' | 'on-hold' | 'resolved';

/**
 * Maps ANY backend status (from response) to normalized frontend status
 */
export const backendToNormalizedStatus: Record<string, NormalizedTicketStatus> = {
  // Response API statuses
  open: NormalizedTicketStatus.NEW,
  pending: NormalizedTicketStatus.AWAITING_REPLY,
  on_hold: NormalizedTicketStatus.ON_HOLD,
  ['on-hold']: NormalizedTicketStatus.ON_HOLD,
  solved: NormalizedTicketStatus.SOLVED,
  closed: NormalizedTicketStatus.SOLVED,
  // Filter API statuses (some overlap)
  // T-7: QA says in_progress → New (not On Hold)
  in_progress: NormalizedTicketStatus.NEW,
  waiting: NormalizedTicketStatus.AWAITING_REPLY,
  resolved: NormalizedTicketStatus.SOLVED,
};

/**
 * Maps normalized status to array of backend filter statuses for API requests
 * When user selects "New", API must receive ["open"] (backend filter key)
 */
export const normalizedToFilterStatuses: Record<NormalizedTicketStatus, string[]> = {
  [NormalizedTicketStatus.NEW]: ['open', 'in_progress'],
  [NormalizedTicketStatus.AWAITING_REPLY]: ['waiting'],
  [NormalizedTicketStatus.ON_HOLD]: ['on-hold'],
  [NormalizedTicketStatus.SOLVED]: ['resolved', 'closed'],
};

/**
 * Filter options shown in UI - uses normalized status as key
 */
export const ticketFilterOptions: Array<{
  key: NormalizedTicketStatus;
  labelKey: string;
}> = [
  { key: NormalizedTicketStatus.NEW, labelKey: 'NEW' },
  { key: NormalizedTicketStatus.AWAITING_REPLY, labelKey: 'AWAITING_REPLY' },
  { key: NormalizedTicketStatus.ON_HOLD, labelKey: 'ON_HOLD' },
  { key: NormalizedTicketStatus.SOLVED, labelKey: 'SOLVED' },
];

/**
 * Normalizes any backend status to a frontend normalized status
 * @param rawStatus - Status string from backend API response
 * @returns Normalized status for frontend use
 */
export function normalizeTicketStatus(rawStatus?: string): NormalizedTicketStatus {
  if (!rawStatus) return NormalizedTicketStatus.NEW;
  const normalized = backendToNormalizedStatus[rawStatus.toLowerCase()];
  return normalized ?? NormalizedTicketStatus.NEW;
}

/**
 * Maps selected normalized filter statuses to backend API filter values
 * @param selectedFilters - Array of normalized statuses selected by user
 * @returns Array of backend filter status values to send to API
 */
export function mapFilterToApiStatuses(selectedFilters: NormalizedTicketStatus[]): string[] {
  const apiStatuses: string[] = [];
  selectedFilters.forEach((filter) => {
    const backendStatuses = normalizedToFilterStatuses[filter];
    if (backendStatuses) {
      apiStatuses.push(...backendStatuses);
    }
  });
  return apiStatuses;
}

/**
 * Gets tag color and icon data for a normalized status
 * Uses a function to avoid circular dependency issues with THEME_VARIABLE
 */
function getStatusTagData(status: NormalizedTicketStatus): { iconColor: string; tagColor: string } {
  switch (status) {
    case NormalizedTicketStatus.NEW:
      return {
        iconColor: THEME_VARIABLE?.customDarkBlue ?? '#1890ff',
        tagColor: 'purple',
      };
    case NormalizedTicketStatus.AWAITING_REPLY:
      return {
        iconColor: THEME_VARIABLE?.customBlue ?? '#1890ff',
        tagColor: 'processing',
      };
    case NormalizedTicketStatus.ON_HOLD:
      return {
        iconColor: THEME_VARIABLE?.customOrange ?? '#fa8c16',
        tagColor: 'orange',
      };
    case NormalizedTicketStatus.SOLVED:
      return {
        iconColor: THEME_VARIABLE?.customGreen ?? '#52c41a',
        tagColor: 'success',
      };
    default:
      return {
        iconColor: THEME_VARIABLE?.customBlue ?? '#1890ff',
        tagColor: 'processing',
      };
  }
}

/**
 * Gets tag display data for a given backend status
 * @param rawStatus - Status string from backend API
 * @returns Tag color and icon color for display
 */
export function getTicketStatusTagData(rawStatus?: string): {
  iconColor: string;
  tagColor: string;
} {
  const normalized = normalizeTicketStatus(rawStatus);
  return getStatusTagData(normalized);
}

/**
 * Gets translation key for a normalized status
 * @param status - Normalized status
 * @returns Translation key for i18n
 */
export function getStatusTranslationKey(status: NormalizedTicketStatus): string {
  return `ticket_status.${status}`;
}

/**
 * Gets translation key for a raw backend status
 * @param rawStatus - Status string from backend API
 * @returns Translation key for i18n
 */
export function getStatusTranslationKeyFromRaw(rawStatus?: string): string {
  const normalized = normalizeTicketStatus(rawStatus);
  return getStatusTranslationKey(normalized);
}
